<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("reshuffle_competitors_modal.title") }}</template>
        <template v-slot:body>
            <div v-if="fights.length > 0 && initDone">
                <div class="is-size-5 mb-1">
                    <span class="is-description">{{ translate("common.category") }}:</span> {{ getCategoryName() }}
                </div>
                <div class="is-size-5 mb-3" v-if="firstFight.id">
                    <span class="is-description">{{ translate("common.round") }}:</span>
                    <FightRoundName :bracket-level="firstFight.bracketLevel" :round-name="firstFight.roundName" />
                </div>

                <div class="mb-1">{{ translate("reshuffle_competitors_modal.pick_competitors") }}</div>
                <div
                    v-for="(fight, index) in fights"
                    class="box is-condensed is-flex is-align-items-center"
                    :key="fight.id"
                    :class="{ 'is-disabled': !fight.isReadyToPlay }"
                >
                    <div>
                        <FightNumber :fight-index="index" :fight="fight" />
                    </div>
                    <div class="is-flex is-justify-content-space-around is-flex-grow-1">
                        <div class="is-flex">
                            <SingleCompetitor
                                :selectable="canBeSelected(fight.firstCompetitor.id)"
                                :competitor="fight.firstCompetitor"
                                @selected="onCompetitorSelected(fight.firstCompetitor.id, fight.id)"
                            />
                            <span class="icon" v-if="selectedCompetitors[fight.firstCompetitor.id]">
                                <i class="fa fa-check" />
                            </span>
                        </div>
                        <div class="is-flex">
                            <SingleCompetitor
                                :selectable="canBeSelected(fight.secondCompetitor.id)"
                                :competitor="fight.secondCompetitor"
                                @selected="onCompetitorSelected(fight.secondCompetitor.id, fight.id)"
                            />
                            <span class="icon" v-if="selectedCompetitors[fight.secondCompetitor.id]">
                                <i class="fa fa-check" />
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="initDone">
                <Notification class="is-danger">
                    <span class="icon">
                        <i class="fa fa-exclamation-triangle" />
                    </span>
                    <span>{{ translate("reshuffle_competitors_modal.no_matches") }}</span>
                </Notification>
            </div>
            <div v-else class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <button class="button is-primary" :disabled="!canBeConfirmed" @click="confirm">
                    {{ translate("common.ok") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../Common/RegularModal.vue";
    import { useModal } from "../Common/useModal";
    import { computed, ref } from "vue";
    import fightsApi from "../Api/fights";
    import SingleCompetitor from "../Common/Fights/SingleCompetitor.vue";
    import FightNumber from "../Common/Fights/FightNumber.vue";
    import FightRoundName from "../Common/FightRoundName.vue";
    import { translate } from "../Common/translations";
    import Notification from "../Common/Notification.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import { ComplexFight } from "../Common/Fights/types";

    const props = defineProps<{
        eventId: number;
    }>();

    const { modal, open, close } = useModal();
    const fightId = ref<number | null>(null);
    const initDone = ref(false);

    const sourceFightId = ref<number>(0);
    const fights = ref<ComplexFight[]>([]);
    const firstFight = computed(() => {
        return fights.value[0] ?? {};
    });

    function openForFight(fightIdToOpen: number) {
        initDone.value = false;
        fightId.value = fightIdToOpen;
        fights.value = [];
        sourceFightId.value = 0;
        selectedCompetitors.value = {};
        open();

        return fightsApi
            .getRelevantFightsForReshuffle(props.eventId, fightId.value)
            .then(fightsResponse => {
                fights.value = fightsResponse.fights;
                sourceFightId.value = fightsResponse.sourceFightId;
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    defineExpose({ openForFight });

    function getCategoryName() {
        if (fights.value.length === 0) {
            return "-";
        }

        return fights.value[0].category;
    }

    // SELECTING COMPETITORS

    const selectedCompetitors = ref({});

    function onCompetitorSelected(competitorId, selectedCFightId) {
        if (selectedCompetitors.value[competitorId]) {
            selectedCompetitors.value[competitorId] = false;
        } else {
            selectedCompetitors.value[competitorId] = selectedCFightId;
        }
    }

    function canBeSelected(competitorId) {
        //If competitor is already selected, we can deselect.
        if (selectedCompetitors.value[competitorId]) {
            return true;
        }

        //Only one competitor from single fight can be selected
        const fight = fights.value.find(f => f.firstCompetitor.id === competitorId || f.secondCompetitor.id === competitorId);

        if (!fight || !fight.isReadyToPlay) {
            return false;
        }

        return !selectedCompetitors.value[fight.firstCompetitor.id] && !selectedCompetitors.value[fight.secondCompetitor.id];
    }

    // CONFIRMING SELECTION

    const canBeConfirmed = computed(() => {
        //Exactly two values selected to true
        return Object.values(selectedCompetitors.value).filter(v => v).length === 2;
    });

    const emit = defineEmits(["reshuffleConfirmed"]);

    function confirm() {
        if (!canBeConfirmed.value) {
            return;
        }

        const competitorIds = Object.keys(selectedCompetitors.value).filter(id => selectedCompetitors.value[id]);

        return fightsApi
            .reshuffleCompetitors(props.eventId, {
                parameterId: Number(firstFight.value.parameterId),
                firstFightId: Number(selectedCompetitors.value[competitorIds[0]]),
                secondFightId: Number(selectedCompetitors.value[competitorIds[1]]),
                firstCompetitorId: Number(competitorIds[0]),
                secondCompetitorId: Number(competitorIds[1]),
            })
            .then(() => {
                emit("reshuffleConfirmed", firstFight.value.matId);
                successToast(translate("reshuffle_competitors_modal.successfully_reshuffled"));
                close();
            })
            .catch(handleError);
    }
</script>

<style scoped></style>
